import React from "react";
import { motion } from "framer-motion";

// Metodo page: small table-lamp icons in place of the step numbers, original drawings inspired by four design lamps
// (1 articulated arm, 2 dome on a conical base, 3 soft cocoon dome on a stand, 4 table lamp with a wide shade). They switch on when
// the step comes into view and glow brighter on hover / tap.
const Light = ({ children }) => (
  <motion.g className="step-lamp-light" initial={{ opacity: 0.12 }} whileInView={{ opacity: [0.12, 1, 0.45, 1] }}
    viewport={{ once: true, margin: "-40px" }} transition={{ duration: 1.4, delay: 0.35, times: [0, 0.35, 0.55, 1] }}>
    {children}
  </motion.g>
);

const Glow = ({ id }) => (
  <defs>
    <radialGradient id={id}>
      <stop offset="0%" stopColor="#FFE3A1" stopOpacity="0.95" />
      <stop offset="60%" stopColor="#FFC861" stopOpacity="0.35" />
      <stop offset="100%" stopColor="#FFC861" stopOpacity="0" />
    </radialGradient>
    <linearGradient id={`${id}-beam`} x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="#FFD27A" stopOpacity="0.85" />
      <stop offset="100%" stopColor="#FFD27A" stopOpacity="0" />
    </linearGradient>
  </defs>
);

// 1: articulated arm desk lamp, aluminium
const ArmLamp = () => (
  <>
    <Glow id="step-lamp-1" />
    <Light>
      <path d="M33 21 L41 19 L47 45 L27 45 Z" fill="url(#step-lamp-1-beam)" />
      <circle cx="37.5" cy="20" r="5" fill="url(#step-lamp-1)" />
    </Light>
    <ellipse cx="13" cy="44.5" rx="7" ry="1.6" fill="#7E8790" />
    <path d="M13 44 L18 23 L35 13" fill="none" stroke="#A9B2BA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M15 40 L22 21 M18 23 L33 9" stroke="#C7CED4" strokeWidth="0.8" strokeLinecap="round" />
    <circle cx="18" cy="23" r="1.8" fill="#7E8790" />
    <path d="M32 12 L38 9 L42 18 L34 21 Z" fill="#B9C1C8" stroke="#7E8790" strokeWidth="0.8" strokeLinejoin="round" />
  </>
);

// 2: dome on a conical base, warm gold
const DomeLamp = () => (
  <>
    <Glow id="step-lamp-2" />
    <Light>
      <ellipse cx="24" cy="20" rx="17" ry="6" fill="url(#step-lamp-2)" />
    </Light>
    <rect x="17" y="34" width="14" height="10.5" rx="1" fill="#B98E3E" />
    <path d="M17 34 L31 34 L24.8 23 L23.2 23 Z" fill="#CFA752" />
    <path d="M10 19.5 A14 12 0 0 1 38 19.5 Z" fill="#D9B25E" stroke="#A97F33" strokeWidth="0.8" strokeLinejoin="round" />
    <path d="M14 14 A10 8 0 0 1 22 10" fill="none" stroke="#F2D896" strokeWidth="1.2" strokeLinecap="round" />
  </>
);

// 3: soft cocoon dome on a slim stand, the shade itself lights up
const CocoonLamp = () => (
  <>
    <Glow id="step-lamp-3" />
    <path d="M24 31 L24 43" stroke="#5A534C" strokeWidth="1.6" />
    <path d="M19 44 L24 41 L29 44" fill="none" stroke="#5A534C" strokeWidth="1.6" strokeLinejoin="round" />
    <path d="M7 30 C7 11 41 11 41 30 C41 33.5 7 33.5 7 30 Z" fill="#F4ECDD" stroke="#D6C6A8" strokeWidth="0.9" />
    <Light>
      <ellipse cx="24" cy="24" rx="19" ry="13" fill="url(#step-lamp-3)" />
      <path d="M10 29 C10 15 38 15 38 29 C38 31.5 10 31.5 10 29 Z" fill="#FFD98A" opacity="0.75" />
    </Light>
    <path d="M12 22 C14 18 18 16 22 15.5" fill="none" stroke="#FFFFFF" strokeOpacity="0.7" strokeWidth="1.1" strokeLinecap="round" />
  </>
);

// 4: table lamp with a wide terracotta shade on a slim stem, light falling below the shade
const ShadeLamp = () => (
  <>
    <Glow id="step-lamp-4" />
    <Light>
      <path d="M12 24 L36 24 L44 44 L4 44 Z" fill="url(#step-lamp-4-beam)" />
      <ellipse cx="24" cy="23" rx="14" ry="6" fill="url(#step-lamp-4)" />
    </Light>
    <ellipse cx="24" cy="44.3" rx="8" ry="1.7" fill="#8E3F2A" />
    <path d="M24 24 L24 43" stroke="#8E3F2A" strokeWidth="1.6" />
    <path d="M17 8 L31 8 L38 23 C33 25.5 15 25.5 10 23 Z" fill="#D06A4C" stroke="#A84E35" strokeWidth="0.9" strokeLinejoin="round" />
    <path d="M18 10.5 L14 20" stroke="#E99A7E" strokeWidth="1.1" strokeLinecap="round" />
    <Light>
      <ellipse cx="24" cy="23.6" rx="9" ry="1.6" fill="#FFD98A" />
    </Light>
  </>
);

const LAMPS = [ArmLamp, DomeLamp, CocoonLamp, ShadeLamp];

export const StepLamp = ({ index, testid }) => {
  const Lamp = LAMPS[index % LAMPS.length];
  return (
    <span data-testid={testid} className="step-lamp inline-flex items-end">
      <svg viewBox="0 0 48 48" className="w-12 h-12 sm:w-14 sm:h-14" aria-hidden="true" focusable="false"><Lamp /></svg>
      <span className="sr-only">{String(index + 1).padStart(2, "0")}</span>
    </span>
  );
};
